import React, { useEffect, useState } from 'react';
import { Table, FormattedMessage, useIntl } from '@metsooutotec/modes-react-components';
import TableFilterBox from './table-filter-box';
import  '../list-view/list-view-styles.css';
import { observer } from 'mobx-react-lite';

interface HasId {
    id: string;
}

export interface TemplateItem<T extends HasId> {
    headingKey: string;
    headingCellfun?: () => React.ReactNode | string;
    cellFunc: (x: T) => React.ReactNode | string;
    isSearchable?: boolean;
}

interface ListViewProps<T extends HasId> {
    noneYetKey: string;
    template: TemplateItem<T>[];
    data: T[];
    onClickRow?: (item: T) => void;
    selectedRow?: number
}

// This is a helper function to filter the data based on the filters applied.
const applyFilters = (data: any[], columnFilters: { [key: number]: string }, template: TemplateItem<any>[]) => {
    let filtered = [...data];
    Object.keys(columnFilters).forEach((key) => {
        const columnIndex = parseInt(key, 10);
        const filterValue = columnFilters[columnIndex];
        if (filterValue) {
            filtered = filtered.filter((item) => {
                const cellData = template[columnIndex].cellFunc(item);
                return String(cellData).toLowerCase().includes(filterValue.toLowerCase());
            });
        }
    });
    return filtered;
};
const ListView = observer(<T extends HasId>(props: ListViewProps<T>) => {

    const { data, noneYetKey, onClickRow, template, selectedRow } = props;
    const { formatMessage } = useIntl();
    const [columnFilters, setColumnFilters] = useState<{ [key: number]: string }>({});
    const [filteredData, setFilteredData] = useState<T[]>(data);
    const modifiedTemplate = [...template];

    useEffect(() => {
        setFilteredData(applyFilters(data, columnFilters, template));
    }, [columnFilters, data, template]);
    
    const handleFilterClick = (value: string, columnIndex: number) => {
        setColumnFilters({
            ...columnFilters,
            [columnIndex]: value,
        });
    };

    // Find the index of the column with headingKey "headings.icon"
    const iconColumnIndex = modifiedTemplate.findIndex(t => t.headingKey === "headings.icon");
    // Move the column with headingKey "headings.icon" to the last position
    if (iconColumnIndex !== -1) {
        const iconColumn = modifiedTemplate.splice(iconColumnIndex, 1)[0];
        modifiedTemplate.push(iconColumn);
    }

    return (
        <Table
            className="list-view-table"
            hoverable
            onSelectRow={row => onClickRow && onClickRow(filteredData[row])}
            selectable
            selectedRow={selectedRow}
            fixedHeader
            singleLine
        >
            <Table.Header>
                <Table.Row>
                    {template.map((t, index) => (
                        <Table.HeaderCell key={t.headingKey}>
                            <div className="list-view-header">
                                <FormattedMessage id={t.headingKey === "headings.icon" ? "" : t.headingKey} />
                                {t.headingCellfun && t.headingCellfun()}
                                {t.isSearchable && <TableFilterBox onSearch={(value) => handleFilterClick(value, index)} isLast={index === template?.length - 1} />}
                            </div>
                        </Table.HeaderCell>
                    ))}
                </Table.Row>
            </Table.Header>
            <Table.Body>
                {/* New lines: Show "No data found" message in a single cell if no data is found */}
                {filteredData?.length === 0 ? (
                    <Table.Row>
                        <Table.Cell colSpan={template?.length}>
                            {formatMessage(noneYetKey)}
                        </Table.Cell>
                    </Table.Row>
                ) : (
                    filteredData?.map(item => (
                        <Table.Row key={item.id}>
                            {template.map(t => (
                                t.headingKey === "headings.icon" ?
                                    <Table.Cell key={t.headingKey} className="list-view-cell">
                                        <span className="list-view-icon">
                                            {t.cellFunc(item)}
                                        </span>
                                    </Table.Cell>
                                    :
                                    <Table.Cell key={t.headingKey}>
                                        {t.cellFunc(item)}
                                    </Table.Cell>
                            ))}

                        </Table.Row>
                    ))
                )}
            </Table.Body>
        </Table>
    );
});

export default ListView; 
